// Thin fetch wrapper. Auth is stateless — backend issues a JWT on login/register and the
// frontend stores it in localStorage; every request echoes `Authorization: Bearer <token>`.
// No cookies, no CSRF — there's nothing the browser sends automatically that an attacker
// could trick the user's browser into replaying.
//
// Demo mode: while the demo flag is on, no request touches the network — reads resolve
// from src/demo fixtures and writes are rejected with a read-only error.

import { DEMO_READ_ONLY_MESSAGE, exitDemoMode, isDemoMode } from '@/demo/demoMode';

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export type QueryValue = string | number | boolean | undefined | null | Array<string | number | boolean>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  query?: Record<string, QueryValue>;
  organizationId?: string;
  signal?: AbortSignal;
}

const TOKEN_KEY = 'aima.token';

export function getStoredToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// In dev we always go same-origin so the Vite proxy can forward to the backend.
// In prod, VITE_BACKEND_URL (baked in at build time via `.env.prod`) points the SPA
// at an absolute backend origin; empty means same-origin (reverse-proxy deploys).
const API_BASE_URL: string = import.meta.env.PROD
  ? (import.meta.env.VITE_BACKEND_URL ?? '')
  : '';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = API_BASE_URL || window.location.origin;
  const url = new URL(path, base);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item === undefined || item === null || item === '') continue;
          url.searchParams.append(k, String(item));
        }
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }
  return API_BASE_URL ? url.toString() : url.pathname + url.search;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (isDemoMode()) return demoApiRequest<T>(path, options);
  return realApiRequest<T>(path, options);
}

async function demoApiRequest<T>(path: string, options: RequestOptions): Promise<T> {
  const method = options.method ?? 'GET';
  // A short delay keeps spinners/skeletons honest so the demo feels like the real app.
  await new Promise(resolve => setTimeout(resolve, 120 + Math.random() * 180));

  // Signing out of the demo is the one allowed "write": it tears demo mode down.
  if (path === '/api/auth/logout') {
    exitDemoMode();
    return undefined as T;
  }
  if (method !== 'GET') {
    throw new ApiError(403, { error: DEMO_READ_ONLY_MESSAGE }, DEMO_READ_ONLY_MESSAGE);
  }

  // Lazy import keeps the fixture dataset out of the bundle's critical path for real users.
  const { resolveDemoGet } = await import('@/demo/mockApi');
  try {
    return resolveDemoGet(path, options.query ?? {}) as T;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Not available in the demo';
    throw new ApiError(404, { error: message }, message);
  }
}

async function realApiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, organizationId, signal } = options;
  const headers = new Headers({ Accept: 'application/json' });
  if (body !== undefined) headers.set('Content-Type', 'application/json');

  const token = getStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  if (organizationId) headers.set('X-Organization-Id', organizationId);

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const parsed = text ? safeParseJson(text) : undefined;

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof parsed.error === 'string'
        ? parsed.error
        : `${response.status} ${response.statusText}`) || 'Request failed';
    throw new ApiError(response.status, parsed, message);
  }

  return parsed as T;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
